import type { ReactNode } from 'react';
import { BrandLogo } from '@/components/brand/BrandLogo';

/**
 * Shared frame for the signed-out pages (signup, sign in, reset, confirm).
 *
 * Deliberately the viewer's dark palette rather than the admin surface: these
 * pages sit between the marketing site and the replay, and the replay is what
 * people came for.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  showLogo = true,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  showLogo?: boolean;
}) {
  return (
    <div className="min-h-screen bg-viewer-deep px-4 py-10 flex items-center justify-center">
      <div className="w-full max-w-sm">
        {showLogo && (
          <div className="flex justify-center mb-6">
            <BrandLogo size="md" variant="dark" />
          </div>
        )}
        <h1 className="font-viewer-display text-[26px] font-bold text-viewer-ink text-center tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1.5 text-center text-sm text-viewer-muted">{subtitle}</p>
        )}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

/** Inline form-level error. */
export function AuthError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-sm text-viewer-warn">
      {message}
    </p>
  );
}

/**
 * Google sign-in sits in its final position but is not wired up. Drawing the
 * placeholder now means adding the real button later moves nothing on the page.
 */
export function GoogleSlot() {
  return (
    <>
      <div className="flex items-center gap-3 my-4 text-xs text-viewer-faint">
        <span className="h-px flex-1 bg-viewer-line/30" />
        or
        <span className="h-px flex-1 bg-viewer-line/30" />
      </div>
      <div
        aria-hidden="true"
        className="flex items-center justify-center gap-2 w-full rounded-md border border-dashed border-viewer-line2/40 px-4 py-2.5 text-sm text-viewer-faint"
      >
        Continue with Google
        <span className="rounded border border-viewer-line/40 px-1.5 py-px font-viewer-mono text-[9px] uppercase tracking-wider">
          soon
        </span>
      </div>
    </>
  );
}

/** Shared input styling — the UI kit's Input is tuned for the light admin surface. */
export const authFieldClass =
  'w-full rounded-md border border-viewer-line2/40 bg-viewer-panel px-3 py-2.5 text-sm text-viewer-ink ' +
  'placeholder:text-viewer-faint focus:border-viewer-accent focus:outline-none focus:ring-2 focus:ring-viewer-accent/25';

export const authLabelClass = 'block text-xs font-medium text-viewer-muted mb-1.5';

export const authPrimaryButtonClass =
  'w-full rounded-md bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white transition-colors ' +
  'hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-viewer-line2/20 disabled:text-viewer-faint';

export const authGhostButtonClass =
  'w-full rounded-md border border-viewer-line2/40 px-4 py-2.5 text-sm font-medium text-viewer-ink ' +
  'transition-colors hover:bg-viewer-panel2';
